import { Component, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { PartnerService } from 'src/app/api/partner.service';
import { DateTime } from 'luxon';
import { Contact, Note, PartnerLogForStaff, TripDetail } from 'src/app/api';
import { of } from 'rxjs';
import { EditScheduledActivityComponentInputs } from 'src/app/component-modules/notes/edit-scheduled-activity/edit-scheduled-activity.component';
import { DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { IconService } from 'src/app/ui/icon.service';
import { UiModule } from 'src/app/ui/ui.module';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { NotesModule } from 'src/app/component-modules/notes/notes.module';

@Component({
  selector: 'app-partner-scheduled-activities',
  templateUrl: './partner-scheduled-activities.component.html',
  styleUrls: ['./partner-scheduled-activities.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, UiModule, NotesModule]
})
export class PartnerScheduledActivitiesComponent implements OnInit {
  activities = signal<PartnerLogForStaff[]>([]);
  loading = true;
  error: string | null = null;
  showAddActivityButton = false;

  @Input() staffId: number = 0;
  @Input() maxHeight: number = 0;

  constructor(
    private partnerService: PartnerService,
    private dataPadService: DataPadService,
    public icons: IconService,
    private auth: LocalAuthService
  ) {}

  ngOnInit() {
    if (!this.staffId) return;
    this.showAddActivityButton = this.auth.currentUser?.newstaffId === this.staffId;
    this.loadActivities(this.staffId);
  }

  private loadActivities(staffId: number) {
    this.loading = true;
    this.partnerService.getThisWeekPartnerActivitiesForStaff(staffId).subscribe({
      next: (data) => {
        this.activities.set(data);
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load activities';
        this.loading = false;
        console.error('Error loading activities:', err);
      }
    });
  }

  mapToNote(activity: PartnerLogForStaff): Note {
    return {
      id: activity.id,
      custId: Number(activity.partnerId) || 0,
      staffId: activity.staffId,
      notes: activity.notes || '',
      submitted: activity.submitted || new Date(),
      timestamp: activity.timestamp || new Date(),
      logType: activity.logtype || 0,
      flagged: activity.flagged || 0,
      addressed: activity.addressed || 0,
      tripId: activity.tripId,
      category: activity.category || 0,
      callResult: activity.callresult || 0,
      dateScheduled: activity.datescheduled || new Date(),
      dateCompleted: activity.datecompleted,
      priority: activity.priority || 0,
      completed: activity.completed || 0,
      inactive: activity.inactive || 0,
      assignId: activity.assignId || 0,
      ns: activity.ns || 0,
      contactId: Number(activity.contactId) || 0,
      reminderTime: activity.reminderTime,
      reminderType: activity.reminderType || null
    };
  }

  formatDatetime(date: Date): string {
    return DateTime.fromJSDate(new Date(date)).toFormat('MM/dd/yyyy hh:mm a');
  }

  getActivityTypeIcon(logtype: number | undefined): string {
    if (!logtype) return 'fa fa-info-circle';
    
    switch (logtype) {
      case 1: return 'fa fa-sticky-note';
      case 2: return 'fa fa-envelope';
      case 3: return 'fa fa-envelope';
      case 4: return 'fa fa-phone';
      case 5: return 'fa fa-envelope';
      case 6: return 'fa fa-tasks';
      case 7: return 'fa fa-users';
      default: return 'fa fa-info-circle';
    }
  }

  getActivityCategory(category: number | undefined): string {
    if (!category) return 'Unknown';
    
    switch (category) {
      case 1: return 'Initial Contact';
      case 2: return 'Follow Up';
      case 3: return 'Trip Planning';
      case 4: return 'Trip Confirmation';
      case 5: return 'Trip Follow Up';
      case 6: return 'Other';
      default: return 'Unknown';
    }
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    return DateTime.fromJSDate(date).toFormat('MM/dd/yyyy hh:mm a');
  }

  canEditActivity(activity: PartnerLogForStaff): boolean {
    if (!this.staffId) return false;

    const editableTypes = [1, 4, 5, 6, 7];
    return (activity.assignId === this.staffId || activity.staffId === this.staffId) && 
           activity.logtype !== undefined && editableTypes.includes(activity.logtype);
  }

  addActivity() {
    this.dataPadService.showPanel<EditScheduledActivityComponentInputs>(
      { 
        panelName: "editScheduledActivity", 
        inputData: { 
          tripDetails: [], 
          custId: 0, 
          contacts: [], 
          inputObservables: { 
            contacts: of<Contact[]>([]), 
            tripDetails: of<TripDetail[]>([]) 
          } 
        } 
      }
    )
  }
} 